import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  BarChart3,
  TrendingUp,
  FolderOpen,
  Clock,
  ChevronRight,
  LogOut,
  User,
  Search,
  MoreVertical,
  Trash2,
  AlertTriangle,
  FileText,
  Download,
  Sparkles,
  Crown,
  PieChart,
  Settings,
  ShoppingCart,
} from "lucide-react";
import GuidedProjectWizard from "../components/GuidedProjectWizard";
import DMAICPipeline from "../components/DMAICPipeline";
import CreditBadge from "../components/CreditBadge";
import LowCreditBanner from "../components/LowCreditBanner";
import { LogoIcon } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://your-railway-backend-url.railway.app'}/api`;

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProject, setNewProject] = useState({ title: "", description: "", industry: "" });
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    try {
      const [projectsRes, statsRes, subRes] = await Promise.all([
        fetch(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/subscription/current`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
        // Alerts are included in stats response
        setAlerts(statsData.alerts || []);
      }

      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, [token]);

  // Auto-poll stats every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [projectsRes, statsRes] = await Promise.all([
          fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (statsRes.ok) {
          const s = await statsRes.json();
          setStats(s);
          setAlerts(s.alerts || []);
        }
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newProject),
      });

      if (!response.ok) throw new Error("Failed to create project");

      const project = await response.json();
      toast.success("Project created successfully");
      setNewProjectOpen(false);
      setNewProject({ title: "", description: "", industry: "" });
      navigate(`/project/${project.project_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Are you sure you want to delete this project?")) return;

    try {
      const response = await fetch(`${API}/projects/${projectId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to delete project");

      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const downloadExecutiveReport = async (projectId) => {
    try {
      toast.loading("Generating executive report...");
      const response = await fetch(`${API}/projects/${projectId}/export/executive-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to generate report");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `executive_report_${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("Executive report downloaded!");
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const downloadClosureReport = async (projectId) => {
    try {
      toast.loading("Generating closure report...");
      const response = await fetch(`${API}/projects/${projectId}/export/closure-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to generate closure report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `closure_report_${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success("Closure report downloaded!");
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const phaseColors = {
    define: "text-primary",
    measure: "text-[hsl(var(--warning))]",
    analyze: "text-[hsl(var(--info))]",
    improve: "text-[hsl(var(--success))]",
    control: "text-muted-foreground",
    closure: "text-orange-400",
    closed: "text-slate-400",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2">
              <LogoIcon className="w-9 h-9" />
              <span className="font-heading font-bold text-lg">BlackBelt AI</span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <CreditBadge />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem disabled>
                  <User className="w-4 h-4 mr-2" />
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings/profile")} data-testid="profile-settings-link">
                  <Settings className="w-4 h-4 mr-2" />
                  Profile & Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")}>
                  <Crown className="w-4 h-4 mr-2" />
                  {subscription?.plan === "free" ? "Upgrade Plan" : "Manage Plan"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/buy-credits")} data-testid="buy-credits-nav-link">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Buy Credits
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/pricing-intelligence")} data-testid="admin-pricing-intelligence-link">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Pricing Intelligence
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Low Credit Warning */}
        <LowCreditBanner />

        {/* Upgrade Banner for Free Users */}
        {subscription?.plan === "free" && (
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 flex items-center justify-between" data-testid="upgrade-banner">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium">Unlock More Projects & AI Credits</p>
                <p className="text-sm text-muted-foreground">
                  Upgrade to create unlimited projects and get more AI credits
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/pricing")} size="sm" data-testid="upgrade-btn">
              View Plans
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your improvement projects and track progress
              {subscription && subscription.plan !== "free" && (
                <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {subscription.plan_name}
                </span>
              )}
            </p>
          </div>

          <div className="flex gap-3">
            <Link to="/portfolio">
              <Button variant="outline" className="gap-2" data-testid="portfolio-btn">
                <PieChart className="w-4 h-4" />
                Portfolio
              </Button>
            </Link>
            <Link to="/templates">
              <Button variant="outline" className="gap-2" data-testid="templates-btn">
                <FileText className="w-4 h-4" />
                Templates
              </Button>
            </Link>
            
            {/* Simple Create - Quick Option */}
            <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="quick-project-btn">
                  <Plus className="w-4 h-4" />
                  Quick Create
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Project</DialogTitle>
                  <DialogDescription>
                    Start a new DMAIC improvement project
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Project Title</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Reduce Customer Wait Times"
                      value={newProject.title}
                      onChange={(e) =>
                        setNewProject({ ...newProject, title: e.target.value })
                      }
                      required
                      data-testid="project-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      id="description"
                      placeholder="Brief description of the problem"
                      value={newProject.description}
                      onChange={(e) =>
                        setNewProject({ ...newProject, description: e.target.value })
                      }
                      data-testid="project-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry (Optional)</Label>
                    <Input
                      id="industry"
                      placeholder="e.g., Healthcare, Manufacturing"
                      value={newProject.industry}
                      onChange={(e) =>
                        setNewProject({ ...newProject, industry: e.target.value })
                      }
                      data-testid="project-industry-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={creating}
                    data-testid="create-project-btn"
                  >
                    {creating ? "Creating..." : "Create Project"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Guided Wizard - Primary Option */}
            <Button className="gap-2" onClick={() => setWizardOpen(true)} data-testid="new-project-btn">
              <Sparkles className="w-4 h-4" />
              AI-Guided New Project
            </Button>
          </div>
        </div>

        {/* AI Project Alerts */}
        {alerts.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4" data-testid="alerts-section">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <h3 className="font-heading font-semibold text-destructive">Project Alerts</h3>
            </div>
            <div className="space-y-2">
              {alerts.slice(0, 5).map((alert, index) => (
                <div key={index} className="flex items-start gap-3 p-2 bg-background/50 rounded">
                  <div className={`w-2 h-2 rounded-full mt-1.5 ${
                    alert.severity === 'high' ? 'bg-destructive' : 
                    alert.severity === 'medium' ? 'bg-[hsl(var(--warning))]' : 'bg-muted-foreground'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{alert.title}</p>
                    <p className="text-xs text-muted-foreground">{alert.message}</p>
                    {alert.project_id && (
                      <Link 
                        to={`/project/${alert.project_id}`}
                        className="text-xs text-primary hover:underline"
                      >
                        View Project
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-total-projects">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Projects</p>
                  <p className="text-2xl font-bold font-heading">{stats.total_projects}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-active-projects">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[hsl(var(--success))]/10 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[hsl(var(--success))]" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Projects</p>
                  <p className="text-2xl font-bold font-heading">{stats.active_projects}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-savings">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[hsl(var(--warning))]/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[hsl(var(--warning))]" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Potential Savings</p>
                  <p className="text-2xl font-bold font-heading">
                    ${stats.total_potential_savings?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-at-risk">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">At Risk</p>
                  <p className="text-2xl font-bold font-heading">{stats.projects_at_risk}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DMAIC Pipeline View */}
        <DMAICPipeline />

        {/* Projects List */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold">Your Projects</h2>

          {projects.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first DMAIC project to start improving operations
              </p>
              <Button onClick={() => setWizardOpen(true)} data-testid="empty-new-project-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Start AI-Guided Project
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {projects.map((project) => (
                <div
                  key={project.project_id}
                  className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors duration-200"
                  data-testid={`project-card-${project.project_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-semibold">{project.title}</h3>
                        <span
                          className={`text-xs font-medium uppercase tracking-wider ${
                            phaseColors[project.phase]
                          }`}
                        >
                          {project.phase}
                        </span>
                      </div>
                      {project.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {project.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Created {new Date(project.created_at).toLocaleDateString()}
                        {project.industry && ` • ${project.industry}`}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link to={`/project/${project.project_id}`}>
                        <Button variant="ghost" size="sm" className="gap-1" data-testid={`open-project-${project.project_id}`}>
                          Open
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </Link>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => downloadExecutiveReport(project.project_id)}
                            data-testid={`exec-report-${project.project_id}`}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Executive Report
                          </DropdownMenuItem>
                          {project.phase === "closed" && (
                            <DropdownMenuItem
                              onClick={() => downloadClosureReport(project.project_id)}
                              data-testid={`closure-report-${project.project_id}`}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Closure Report
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeleteProject(project.project_id)}
                            data-testid={`delete-project-${project.project_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Project
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Guided Project Wizard */}
      <GuidedProjectWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}
